// @ts-ignore pdfjs-dist legacy worker entry has no bundled declaration.
import pdfjsWorker from 'pdfjs-dist/legacy/build/pdf.worker.entry';
// @ts-ignore pdfjs-dist legacy build is used for Vue CLI 3 compatibility.
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import { PdfReaderConfig } from '../data';

(pdfjsLib as any).GlobalWorkerOptions.workerSrc = pdfjsWorker;

const pdfDocumentPromiseCache: { [key: string]: Promise<any> } = {};

export interface PdfCatalogPage {
  globalPageNumber: number;
  sectionType: PdfSectionType;
  sectionPageNumber: number;
  title: string;
  thumbnail: string;
  loading: boolean;
  failed: boolean;
}

export interface PdfCatalogChapter {
  id: string;
  title: string;
  startPage: number;
  endPage: number;
  pages: PdfCatalogPage[];
}

export type PdfSectionType = 'preface' | 'catalog' | 'content';

export interface PdfSectionSource {
  type: PdfSectionType;
  title: string;
  pdfUrl: string;
}

export interface PdfSectionDocument extends PdfSectionSource {
  document: any;
  pageCount: number;
  startPage: number;
  endPage: number;
}

export interface PdfMergedPage {
  globalPageNumber: number;
  sectionType: PdfSectionType;
  sectionTitle: string;
  sectionPageNumber: number;
  displayTitle: string;
  documentIndex: number;
}

const getPixelRatio = () => window.devicePixelRatio || 1;

const getRenderViewport = (page: any, targetWidth: number) => {
  const viewport = page.getViewport({ scale: 1 });
  const safeWidth = targetWidth > 0 ? targetWidth : viewport.width;
  const scale = safeWidth / viewport.width;

  return page.getViewport({ scale });
};

export const loadPdfDocument = async (url: string) => {
  const safeUrl = sanitizePdfUrl(url);

  if (!safeUrl) {
    throw new Error('Pdf url is empty');
  }

  if (!pdfDocumentPromiseCache[safeUrl]) {
    const task = (pdfjsLib as any).getDocument({
      url: safeUrl,
      useWorkerFetch: false,
    });

    pdfDocumentPromiseCache[safeUrl] = task.promise.catch((error: Error) => {
      delete pdfDocumentPromiseCache[safeUrl];
      throw error;
    });
  }

  return pdfDocumentPromiseCache[safeUrl];
};

export const sanitizePdfUrl = (value: string) => {
  return String(value || '')
    .trim()
    .replace(/^['"`]+/, '')
    .replace(/['"`]+$/, '');
};

export const getPdfSectionSources = (
  config: PdfReaderConfig,
): PdfSectionSource[] => {
  const sections: PdfSectionSource[] = [
    {
      type: 'preface',
      title: '前言',
      pdfUrl: config.prefacePdfUrl,
    },
    {
      type: 'catalog',
      title: '目录',
      pdfUrl: config.catalogPdfUrl,
    },
    {
      type: 'content',
      title: '正文',
      pdfUrl: config.contentPdfUrl,
    },
  ];

  return sections.filter((item) => !!sanitizePdfUrl(item.pdfUrl));
};

export const getSectionDisplayTitle = (
  sectionType: PdfSectionType,
  sectionPageNumber: number,
) => {
  if (sectionType === 'content') {
    return `第 ${sectionPageNumber} 页`;
  }

  if (sectionType === 'catalog') {
    return `目录-${sectionPageNumber}`;
  }

  const baseTitle = sectionType === 'preface' ? '前言' : '目录';

  if (sectionPageNumber === 1) {
    return baseTitle;
  }

  return `${baseTitle}-${sectionPageNumber}`;
};

export const loadPdfSectionDocuments = async (
  config: PdfReaderConfig,
): Promise<PdfSectionDocument[]> => {
  const sections = getPdfSectionSources(config);
  const loadedSections = await Promise.all(
    sections.map(async (section) => {
      const document = await loadPdfDocument(section.pdfUrl);

      return {
        ...section,
        document,
        pageCount: document.numPages,
      };
    }),
  );
  const documents: PdfSectionDocument[] = [];
  let startPage = 1;

  for (let index = 0; index < loadedSections.length; index += 1) {
    const section = loadedSections[index];

    documents.push({
      ...section,
      startPage,
      endPage: startPage + section.pageCount - 1,
    });

    startPage += section.pageCount;
  }

  return documents;
};

export const createMergedPages = (
  sectionDocuments: PdfSectionDocument[],
): PdfMergedPage[] => {
  const pages: PdfMergedPage[] = [];

  for (
    let documentIndex = 0;
    documentIndex < sectionDocuments.length;
    documentIndex += 1
  ) {
    const sectionDocument = sectionDocuments[documentIndex];

    for (
      let sectionPageNumber = 1;
      sectionPageNumber <= sectionDocument.pageCount;
      sectionPageNumber += 1
    ) {
      pages.push({
        globalPageNumber: sectionDocument.startPage + sectionPageNumber - 1,
        sectionType: sectionDocument.type,
        sectionTitle: sectionDocument.title,
        sectionPageNumber,
        displayTitle: getSectionDisplayTitle(
          sectionDocument.type,
          sectionPageNumber,
        ),
        documentIndex,
      });
    }
  }

  return pages;
};

export const createCatalogChapter = (
  mergedPages: PdfMergedPage[],
): PdfCatalogChapter => {
  return {
    id: 'all-pages',
    title: '全部页面',
    startPage: mergedPages.length ? 1 : 0,
    endPage: mergedPages.length,
    pages: mergedPages.map((item) => ({
      globalPageNumber: item.globalPageNumber,
      sectionType: item.sectionType,
      sectionPageNumber: item.sectionPageNumber,
      title: item.displayTitle,
      thumbnail: '',
      loading: false,
      failed: false,
    })),
  };
};

export const renderPageThumbnail = async (
  pdfDocument: any,
  pageNumber: number,
  targetWidth = 140,
) => {
  const page = await pdfDocument.getPage(pageNumber);
  const viewport = getRenderViewport(page, targetWidth);
  const pixelRatio = getPixelRatio();
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (!context) {
    throw new Error('Canvas context unavailable');
  }

  canvas.width = Math.floor(viewport.width * pixelRatio);
  canvas.height = Math.floor(viewport.height * pixelRatio);
  canvas.style.width = `${viewport.width}px`;
  canvas.style.height = `${viewport.height}px`;
  context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);

  await page.render({
    canvasContext: context,
    viewport,
  }).promise;

  return canvas.toDataURL('image/jpeg', 0.82);
};

export const renderPageImage = async (
  pdfDocument: any,
  pageNumber: number,
  targetWidth: number,
  quality = 0.9,
) => {
  const page = await pdfDocument.getPage(pageNumber);
  const viewport = getRenderViewport(page, targetWidth);
  const pixelRatio = getPixelRatio();
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (!context) {
    throw new Error('Canvas context unavailable');
  }

  canvas.width = Math.floor(viewport.width * pixelRatio);
  canvas.height = Math.floor(viewport.height * pixelRatio);
  canvas.style.width = `${viewport.width}px`;
  canvas.style.height = `${viewport.height}px`;
  context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);

  await page.render({
    canvasContext: context,
    viewport,
  }).promise;

  return canvas.toDataURL('image/jpeg', quality);
};

export const getPageViewportSize = async (
  pdfDocument: any,
  pageNumber: number,
) => {
  const page = await pdfDocument.getPage(pageNumber);
  const viewport = page.getViewport({ scale: 1 });

  return {
    width: viewport.width,
    height: viewport.height,
  };
};

export const getNeighborPageNumbers = (
  pageNumber: number,
  totalPages: number,
  radius: number,
) => {
  const pages: number[] = [];
  const safeRadius = Math.max(radius, 0);
  const startPage = Math.max(pageNumber - safeRadius, 1);
  const endPage = Math.min(pageNumber + safeRadius, totalPages);

  for (let currentPage = startPage; currentPage <= endPage; currentPage += 1) {
    pages.push(currentPage);
  }

  return pages;
};

export const renderPageToCanvas = async (
  pdfDocument: any,
  pageNumber: number,
  canvas: HTMLCanvasElement,
  targetWidth: number,
) => {
  const page = await pdfDocument.getPage(pageNumber);
  const viewport = getRenderViewport(page, targetWidth);
  const pixelRatio = getPixelRatio();
  const context = canvas.getContext('2d');

  if (!context) {
    throw new Error('Canvas context unavailable');
  }

  canvas.width = Math.floor(viewport.width * pixelRatio);
  canvas.height = Math.floor(viewport.height * pixelRatio);
  canvas.style.width = `${viewport.width}px`;
  canvas.style.height = `${viewport.height}px`;
  context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
  context.clearRect(0, 0, viewport.width, viewport.height);

  await page.render({
    canvasContext: context,
    viewport,
  }).promise;
};
